const selector = '[data-merowen-track-order-section="lookup"]';
const instances = new WeakMap();

class MerowenTrackOrderLookup {
  constructor(root) {
    this.root = root;
    this.controller = new AbortController();
    this.tabs = [...root.querySelectorAll('[data-merowen-track-order-tab]')];
    this.panels = [...root.querySelectorAll('[data-merowen-track-order-panel]')];
    this.forms = [...root.querySelectorAll('[data-merowen-track-order-form]')];
    this.bind();
  }

  bind() {
    const options = { signal: this.controller.signal };
    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => this.activate(tab.dataset.merowenTrackOrderTab, true), options);
      tab.addEventListener('keydown', (event) => this.onTabKeydown(event), options);
    });
    this.forms.forEach((form) => form.addEventListener('submit', (event) => this.onSubmit(event), options));
  }

  activate(name, focusTab = false) {
    this.tabs.forEach((tab) => {
      const active = tab.dataset.merowenTrackOrderTab === name;
      tab.classList.toggle('is-active', active);
      tab.setAttribute('aria-selected', String(active));
      tab.tabIndex = active ? 0 : -1;
      if (active && focusTab) tab.focus();
    });
    this.panels.forEach((panel) => {
      panel.hidden = panel.dataset.merowenTrackOrderPanel !== name;
    });
  }

  onTabKeydown(event) {
    const current = this.tabs.indexOf(event.currentTarget);
    let next = current;
    if (event.key === 'ArrowRight') next = (current + 1) % this.tabs.length;
    if (event.key === 'ArrowLeft') next = (current - 1 + this.tabs.length) % this.tabs.length;
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = this.tabs.length - 1;
    if (next !== current) {
      event.preventDefault();
      this.activate(this.tabs[next].dataset.merowenTrackOrderTab, true);
    }
  }

  onSubmit(event) {
    const form = event.currentTarget;
    const inputs = [...form.querySelectorAll('input[required]')];
    let firstInvalid = null;
    inputs.forEach((input) => {
      const value = input.value.trim();
      input.value = value;
      const invalid = value.length === 0;
      const error = document.getElementById(input.getAttribute('aria-describedby'));
      input.setAttribute('aria-invalid', String(invalid));
      if (error) error.hidden = !invalid;
      if (invalid && !firstInvalid) firstInvalid = input;
    });
    if (firstInvalid) {
      event.preventDefault();
      firstInvalid.focus();
      return;
    }
    const button = form.querySelector('[data-merowen-track-order-submit]');
    if (button) {
      button.disabled = true;
      button.textContent = button.dataset.submittingLabel;
    }
  }

  resetSubmitting() {
    this.forms.forEach((form) => {
      const button = form.querySelector('[data-merowen-track-order-submit]');
      if (!button) return;
      button.disabled = false;
      button.textContent = button.dataset.defaultLabel;
    });
  }

  destroy() {
    this.controller.abort();
  }
}

function init(root = document) {
  const elements = [...root.querySelectorAll(selector)];
  if (root.matches?.(selector)) elements.unshift(root);
  elements.forEach((element) => {
    if (instances.has(element)) return;
    instances.set(element, new MerowenTrackOrderLookup(element));
  });
}

function destroy(root) {
  const elements = [...root.querySelectorAll(selector)];
  if (root.matches?.(selector)) elements.unshift(root);
  elements.forEach((element) => {
    instances.get(element)?.destroy();
    instances.delete(element);
  });
}

function findLookup() {
  return document.querySelector(selector);
}

document.addEventListener('click', (event) => {
  const link = event.target.closest('[data-merowen-track-order-form-link]');
  if (!link) return;
  const lookup = findLookup();
  if (!lookup) return;
  event.preventDefault();
  lookup.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
  const activeTab = lookup.querySelector('[data-merowen-track-order-tab][aria-selected="true"]');
  window.setTimeout(() => activeTab?.focus(), 0);
});

window.addEventListener('pageshow', () => {
  init();
  document.querySelectorAll(selector).forEach((element) => instances.get(element)?.resetSubmitting());
});

document.addEventListener('shopify:section:load', (event) => init(event.target));
document.addEventListener('shopify:section:unload', (event) => destroy(event.target));
document.addEventListener('shopify:block:select', (event) => {
  const details = event.target.closest('details');
  if (details) details.open = true;
});

init();

// Theme preview hot reload can replace section HTML without re-running a module.
// Initialize inserted lookup roots even when no section:load event is dispatched.
const merowenTrackOrderObserver = new MutationObserver((records) => {
  for (const record of records) {
    for (const node of record.removedNodes) {
      if (node.nodeType === Node.ELEMENT_NODE) destroy(node);
    }
    for (const node of record.addedNodes) {
      if (node.nodeType === Node.ELEMENT_NODE) init(node);
    }
  }
});
merowenTrackOrderObserver.observe(document.documentElement, { childList: true, subtree: true });
document.addEventListener('DOMContentLoaded', () => init(), { once: true });
